import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, ArrowRight, Check, Sparkles, CalendarDays } from 'lucide-react';
import Layout from '../components/Layout';
import NotFound from './NotFound';
import { useApp } from '../context/AppContext';
import { guideBySlug, guideCopy, guides } from '../mock/guides';
import { buildSections } from '../mock/blogContent';
import { currencySymbols, rates } from '../mock/mock';
import useSEO from '../hooks/useSEO';

const catFor = (g) => {
  if (g.type === 'route' || g.type === 'destination') return 'route';
  if (g.type === 'service') {
    const id = g.service && g.service.id;
    if (id === 'ski') return 'ski';
    if (id === 'chauffeur' || id === 'holiday' || id === 'intercity') return 'business';
    return 'airport';
  }
  return 'airport';
};

const GuidePage = () => {
  const { slug } = useParams();
  const { lang, currency } = useApp();
  const g = guideBySlug(slug);
  const c = g ? guideCopy(g, lang) : null;
  const rich = g ? buildSections(c.title, catFor(g), lang) : null;
  const fmt = (n) => `${currencySymbols[currency]} ${Math.round(n * rates[currency])}`;
  useSEO(c ? `${c.title} | CAB.TAXI` : 'CAB.TAXI', c ? c.intro : '', { skip: !g });

  if (!g) return <NotFound />;

  const bookHref = g.from ? `/booking?from=${encodeURIComponent(g.from)}${g.to ? `&to=${encodeURIComponent(g.to)}` : ''}` : '/booking';
  const related = guides.filter((x) => x.slug !== g.slug && x.type === g.type).slice(0, 3);

  return (
    <Layout bookingTop>
      {/* Hero */}
      <section className="relative" data-testid={`guide-hero-${g.slug}`}>
        <img src={g.img} alt={c.title} className="w-full h-[42vh] min-h-[320px] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 w-full pb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-xs font-semibold tracking-wide uppercase text-white mb-4">
              <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--brand-accent)' }} /> {c.tag}
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight max-w-3xl">{c.title}</h1>
            {g.type === 'route' && (
              <div className="mt-4 inline-flex items-center gap-2 text-white/90 font-semibold">
                <MapPin className="w-4 h-4" style={{ color: 'var(--brand-accent)' }} /> {g.from} <ArrowRight className="w-4 h-4" /> {g.to}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1fr_320px] gap-10 items-start">
          {/* Article */}
          <div>
            <p className="text-lg text-gray-600 leading-relaxed">{c.intro}</p>
            <div className="mt-8 space-y-8">
              {rich.sections.map((s, i) => (
                <div key={i} data-testid={`guide-section-${i}`}>
                  <h2 className="text-xl font-extrabold text-[color:var(--brand-dark)] mb-2">{s.h}</h2>
                  <div className="space-y-3">
                    {s.paras.map((p, j) => <p key={j} className="text-gray-600 leading-relaxed">{p}</p>)}
                  </div>
                </div>
              ))}
            </div>

            {/* FAQ */}
            <div className="mt-10" data-testid="guide-faq">
              <h2 className="text-xl font-extrabold text-[color:var(--brand-dark)] mb-4">{rich.labels.faq}</h2>
              <div className="space-y-3">
                {rich.faqs.map((f) => (
                  <div key={f.q} className="rounded-xl border border-gray-100 bg-white p-4">
                    <div className="font-bold text-[color:var(--brand-dark)]">{f.q}</div>
                    <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Related */}
            {related.length > 0 && (
              <div className="mt-12">
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4">{{ de: 'Mehr entdecken', en: 'Discover more', fr: 'À découvrir', it: 'Scopri di più' }[lang]}</h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  {related.map((r) => (
                    <Link key={r.slug} to={`/guide/${r.slug}`} className="card-hover group rounded-xl overflow-hidden bg-white border border-gray-100" data-testid={`guide-related-${r.slug}`}>
                      <img src={r.img} alt="" className="w-full h-24 object-cover" />
                      <div className="p-3 text-sm font-bold text-[color:var(--brand-dark)] leading-snug">{guideCopy(r, lang).title}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Booking card */}
          <aside className="rounded-2xl bg-[color:var(--brand-muted)] p-6 lg:sticky lg:top-24" data-testid="guide-booking-card">
            {g.type === 'route' && (
              <div className="mb-4">
                <div className="text-xs font-bold uppercase tracking-widest text-gray-400">{c.priceFrom}</div>
                <div className="text-4xl font-extrabold text-[color:var(--brand-dark)]">{fmt(g.price)}</div>
              </div>
            )}
            <ul className="space-y-2 mb-6">
              {['Festpreis', 'Professioneller Chauffeur', 'Flugverfolgung', 'Gratis Stornierung'].map((p) => (
                <li key={p} className="flex items-center gap-2 text-sm text-[color:var(--brand-dark)]"><Check className="w-4 h-4" style={{ color: 'var(--brand-accent)' }} /> {p}</li>
              ))}
            </ul>
            <Link to={bookHref} className="accent-btn w-full rounded-xl py-3 font-bold flex items-center justify-center gap-2" data-testid="guide-book-btn">
              <CalendarDays className="w-4 h-4" /> {c.book}
            </Link>
          </aside>
        </div>
      </section>
    </Layout>
  );
};

export default GuidePage;
